import React, { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { FormikField } from '../../../../../../components/FormikField';
import { getInputType, getTaxTypes } from '../../helpers';
import { getDiscountFormInitialValues } from '../../formConfig/initialValues';
import { Column } from 'primereact/column';
import {
  DISCOUNT_LINE_ITEMS_COLUMNS,
  getDiscountFieldProps,
  getDiscountTypes,
} from './DiscountDialog.config';
import { discountValidationSchema } from '../../formConfig/validations';
import { v4 as uuid } from 'uuid';
import styles from './DiscountDialog.styles.scss';
import { normalizeInvoiceLevelDiscountItem } from '../../../../../../helpers/invoiceHelpers';
import { DataTable } from 'primereact/datatable';
import cn from 'classnames';
const getRateFromValue = (value) => {
  const taxType = getTaxTypes('en', false).find((t) => t.value === value);
  return taxType?.rate || '';
};

const DiscountDialog = ({ items, isOpen, setIsOpen, onClose, values }) => {
  const { t, i18n } = useTranslation();
  const [savedItems, setSavedItems] = useState(items || []);
  const [editingRows, setEditingRows] = useState([]);

  const handleAddItemClick = ({ values, setFieldValue }) => {
    const newItem = normalizeInvoiceLevelDiscountItem({
      id: uuid(),
      ...values.tempItem,
      taxExemptionReason: null,
      discountVatRate: getRateFromValue(values.tempItem.discountVatRateType),
    });
    const lineItems = [...values.items, newItem];

    setFieldValue('items', lineItems);
    setFieldValue('tempItem', getDiscountFormInitialValues(items).tempItem);
    setSavedItems([...lineItems]);
  };

  const isColumnEditableForType = (colKey, itemType) => {
    if (itemType === 'amount') {
      return colKey === 'discountAmount' || colKey === 'discountVatRateType';
    }
    if (itemType === 'subject_amount') {
      return (
        colKey === 'amountSubjectToDiscount' ||
        colKey === 'rate' ||
        colKey === 'discountVatRateType'
      );
    }
    return true;
  };

  const textEditor = useCallback((options, col, setFieldValue, items) => {
    const itemType = items[options.rowIndex]?.type;
    if (!isColumnEditableForType(col.key, itemType)) {
      return items[options.rowIndex]?.[col.key] ?? '-';
    }
    return (
      <FormikField
        name={`items[${options.rowIndex}][${col.key}]`}
        key={col.key + options.rowIndex}
        component={getInputType(col.type)}
        showError={false}
        controlProps={{
          labelProps: { className: styles.label },
        }}
        {...getDiscountFieldProps(i18n.language, t, (e) =>
          setFieldValue(`items[${options.rowIndex}][${col.key}]`, e),
        )[col.key]}
      />
    );
  });

  const rowEditorTemplate = useCallback(
    (rowData, options, values, errors, setFieldValue) => {
      return (
        <div className="d-flex align-items-center">
          {options?.rowEditor?.editing ? (
            <>
              <Button
                icon="pi pi-check"
                rounded
                text
                disabled={errors?.items?.[options.rowIndex]}
                onClick={(e) => {
                  const updatedRow = {
                    ...normalizeInvoiceLevelDiscountItem(rowData),
                    discountVatRate: getRateFromValue(
                      rowData.discountVatRateType,
                    ),
                  };
                  options.rowEditor.onSaveClick(e);
                  setFieldValue(`items[${options.rowIndex}]`, updatedRow);
                  const updatedItems = savedItems.map((item, index) =>
                    index === options.rowIndex ? updatedRow : item,
                  );
                  setSavedItems(updatedItems);
                  setEditingRows((prev) =>
                    prev.filter((id) => id !== options.dataKey),
                  );
                }}
                type="button"
              />
              <Button
                icon="pi pi-times"
                rounded
                text
                onClick={(e) => {
                  options.rowEditor.onCancelClick(e);
                  setFieldValue(
                    `items[${options.rowIndex}]`,
                    savedItems[options.rowIndex],
                  );
                  setEditingRows((prev) =>
                    prev.filter((id) => id !== options.dataKey),
                  );
                }}
                type="button"
              />
            </>
          ) : (
            <>
              <Button
                icon="pi pi-file-edit"
                rounded
                text
                onClick={(e) => {
                  setEditingRows((prev) => [...prev, options.dataKey]);
                  options.rowEditor.onInitClick(e);
                }}
                type="button"
              />
              <Button
                icon="pi pi-trash"
                rounded
                text
                onClick={() =>
                  handleRowDelete(rowData.id, values, setFieldValue)
                }
                type="button"
              />
            </>
          )}
        </div>
      );
    },
    [savedItems],
  );

  const columnBody = useCallback(
    (rowData, col) => {
      if (col.body) {
        return col.body(i18n.language, rowData[col.key]);
      }
      return rowData[col.key] ? rowData[col.key] : '-';
    },
    [i18n.language],
  );

  const indexColumnBody = useCallback((_data, options) => {
    return options.rowIndex + 1;
  }, []);

  const handleRowDelete = (id, values, setFieldValue) => {
    const itemsP = values.items.filter((item) => item.id !== id);
    setFieldValue('items', itemsP);
  };

  const shouldDisableAddItem = (values, errors) => {
    const { type, ...rest } = values;
    return Object.values(rest).filter((v) => !!v).length === 0 || errors;
  };

  return (
    <Formik
      validateOnMount
      enableReinitialize
      validationSchema={discountValidationSchema({ t })}
      initialValues={getDiscountFormInitialValues(items)}
    >
      {({ values, errors, setFieldValue, resetForm }) => {
        const getTempFieldProps = (fieldName) =>
          getDiscountFieldProps(i18n.language, t, (value) =>
            setFieldValue(`tempItem.${fieldName}`, value),
          )[fieldName];

        return (
          <Form>
            <Dialog
              className={cn(styles.container, 'rounded-4 overflow-hidden w-75')}
              visible={isOpen}
              onHide={() => {
                setIsOpen(false);
                resetForm();
              }}
              header={
                <h3 className={styles.dialogTitle}>
                  {t('createInvoice.discount')}
                </h3>
              }
              footer={
                <div className="pt-3">
                  <Button
                    className="px-5 color-theme-primary"
                    label={t('cancel')}
                    text
                    size="medium"
                    onClick={() => {
                      setIsOpen(false);
                      resetForm();
                      setFieldValue('items', items);
                      setSavedItems(items);
                    }}
                    type="button"
                  />
                  <Button
                    className="px-5"
                    type="button"
                    size="medium"
                    label={t('common.done')}
                    disabled={editingRows.length > 0}
                    onClick={() => {
                      resetForm();
                      onClose({
                        items: values.items,
                      });
                    }}
                  />
                </div>
              }
            >
              <div className="d-flex flex-column gap-4 mb-4">
                <h6>{t('createInvoice.info.einvoiceLevelDiscount.title')}</h6>
                <div
                  className="instructions"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'createInvoice.info.einvoiceLevelDiscount.details',
                    ),
                  }}
                />

                <div>
                  <FormikField
                    label={
                      <label>{t('createInvoice.select_discount_type')}</label>
                    }
                    name="tempItem.type"
                    component={getInputType('radio')}
                    componentProps={{
                      options: getDiscountTypes(t),
                    }}
                  />
                </div>
                <div className="d-flex gap-3 justify-content-evenly">
                  {values.tempItem.type === 'subject_amount' ? (
                    <>
                      <></> {/*Do not remove */}
                      <div className="w-100">
                        <FormikField
                          label={
                            <label>
                              {t('createInvoice.amount_subject_to_discount')}
                            </label>
                          }
                          tooltip={'tooltips.amountSubjectToDiscount'}
                          name="tempItem.amountSubjectToDiscount"
                          component={getInputType('text')}
                          {...getTempFieldProps('amountSubjectToDiscount')}
                        />
                      </div>
                      <div className="w-100">
                        <FormikField
                          label={
                            <label>{t('createInvoice.discount_rate')}</label>
                          }
                          tooltip={'tooltips.discountRate'}
                          name="tempItem.rate"
                          component={getInputType('text')}
                          {...getTempFieldProps('rate')}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="w-100">
                      <FormikField
                        label={
                          <label>{t('createInvoice.discount_amount')}</label>
                        }
                        tooltip={'tooltips.discountAmount'}
                        name="tempItem.discountAmount"
                        component={getInputType('text')}
                        {...getTempFieldProps('discountAmount')}
                      />
                    </div>
                  )}
                </div>
                <div
                  className={
                    values.tempItem.discountVatRateType
                      ? ''
                      : styles.emptySelect
                  }
                >
                  <FormikField
                    name="tempItem.discountVatRateType"
                    label={
                      <label>{t('createInvoice.discount_vat_rate')}</label>
                    }
                    tooltip={'tooltips.discount_vat_rate'}
                    component={getInputType('select')}
                    componentProps={{
                      className: styles.select,
                      options: getTaxTypes(i18n.language),
                    }}
                  />
                </div>
              </div>
              <Button
                type="button"
                className="px-5 mb-4"
                label={t('add_to_list')}
                disabled={shouldDisableAddItem(
                  values.tempItem,
                  errors.tempItem,
                )}
                onClick={() => handleAddItemClick({ values, setFieldValue })}
              />
              <div className={styles.discountTable}>
                <DataTable
                  editMode="row"
                  emptyMessage={' '}
                  value={[...values.items]}
                  tableStyle={{ minWidth: '50rem' }}
                  className="line-items-datatable"
                  dataKey={uuid()}
                >
                  <Column key="id" header="#" body={indexColumnBody} />
                  {DISCOUNT_LINE_ITEMS_COLUMNS({
                    currency: values.currency || 'BHD',
                  }).map((col) => (
                    <Column
                      key={col.key}
                      field={col.key}
                      header={t(col.label)}
                      headerTooltip={
                        col.headerTooltip ? t(col.headerTooltip) : undefined
                      }
                      headerTooltipOptions={{ position: 'top' }}
                      editor={(options) =>
                        textEditor(options, col, setFieldValue, values.items)
                      }
                      body={(data) => columnBody(data, col)}
                    />
                  ))}
                  <Column
                    rowEditor
                    header={t('common.actions')}
                    body={(rowData, options) =>
                      rowEditorTemplate(
                        rowData,
                        options,
                        values,
                        errors,
                        setFieldValue,
                      )
                    }
                  />
                </DataTable>
              </div>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};

export default React.memo(DiscountDialog);
